/*
 * Recommendation tracking (added 2026-10-27, PDP/cart cross-sell plan)
 * ---------------------------------------------------------------------
 * Covers the widgets rendered through <product-recommendations>:
 *   - PDP "You may also like" (related intent)        -> source 'pdp_related'
 *   - PDP "Pairs well with" block (complementary)      -> source 'pdp_complementary'
 *   - Cart page related products                       -> source 'cart_page_related'
 * The cart drawer has its own tracking in assets/cart.js (source 'cart_drawer').
 *
 * dataLayer events:
 *   recommendation_view      once per widget per page view, when >= 50% of it is on screen
 *   recommendation_click     click on a card link (shopper goes to that product page)
 *   cart_recommendation_add  quick-add from a card. Same event name as the cart drawer, so the
 *                            existing GTM -> GA4 tag forwards it with no GTM change;
 *                            recommendation_source tells the widgets apart.
 * Order attribution: when a widget loads, a hidden input properties[_rec_source] (= the source)
 * is added to every add-to-cart form inside it, so the cart line and the order carry it.
 * ProductForm (theme.js) builds its request from new FormData(form), so the input is sent.
 */
// Assigned on window: this file can be included twice on a product page (related section +
// complementary block), and a top-level const would throw on the second load.
window.RecommendationTracking = window.RecommendationTracking || (() => {
  const LISTS = {
    pdp_related: 'pdp_related_products',
    pdp_complementary: 'pdp_complementary_products',
    cart_page_related: 'cart_page_related_products',
  };
  const seen = new WeakSet();
  let observer = null;

  const sourceFor = (element) => {
    const host = element?.closest('product-recommendations');
    if (!host) return null;
    if (host.classList.contains('complementary-products')) return 'pdp_complementary';
    return window.location.pathname.includes('/cart') ? 'cart_page_related' : 'pdp_related';
  };

  const sourceProductId = () => {
    const id = window.ShopifyAnalytics?.meta?.product?.id;
    return id ? String(id) : '';
  };

  const currency = () =>
    window.Shopify?.currency?.active ||
    (typeof FoxTheme !== 'undefined' && FoxTheme.settings?.currencyCode) ||
    '';

  const cardTitle = (card) => {
    const link = card?.querySelector('a[href*="/products/"]');
    return (
      card?.querySelector('.product-card__title')?.textContent?.trim() ||
      link?.getAttribute('aria-label')?.trim() ||
      link?.textContent?.trim() ||
      ''
    );
  };

  const push = (payload) => {
    if (window.Shopify?.designMode) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  const onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || seen.has(entry.target)) return;
      seen.add(entry.target);
      observer?.unobserve(entry.target);
      const source = sourceFor(entry.target);
      if (!source) return;
      push({
        event: 'recommendation_view',
        recommendation_source: source,
        recommendation_list: LISTS[source],
        // Unique products, not card nodes: the slider and nested card markup repeat cards.
        recommendation_count: new Set(
          [...entry.target.querySelectorAll('a[href*="/products/"]')].map((a) => a.pathname)
        ).size,
        source_product_id: sourceProductId(),
      });
    });
  };

  const tagForms = (element) => {
    const source = sourceFor(element);
    if (!source) return;
    element.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      if (form.querySelector('input[name="properties[_rec_source]"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_rec_source]';
      input.value = source;
      form.appendChild(input);
    });
  };

  const observe = (element) => {
    if (!element || seen.has(element)) return;
    if (!('IntersectionObserver' in window)) return;
    observer = observer || new IntersectionObserver(onIntersect, { threshold: 0.5 });
    observer.observe(element);
  };

  const bind = () => {
    if (window.__recommendationTrackingBound) return;
    window.__recommendationTrackingBound = true;

    // Clicks through to a recommended product.
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('product-recommendations a[href*="/products/"]');
        if (!link) return;
        const source = sourceFor(link);
        if (!source) return;
        const card = link.closest('.product-card');
        push({
          event: 'recommendation_click',
          recommendation_source: source,
          recommendation_list: LISTS[source],
          item_name: cardTitle(card),
          link_url: link.href.split('?')[0],
          source_product_id: sourceProductId(),
        });
      },
      true
    );

    // Quick-adds from a recommended card.
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (form && sourceFor(form)) window.__pdpRecommendationPendingForm = form;
      },
      true
    );

    document.addEventListener('product-ajax:added', (event) => {
      const form = window.__pdpRecommendationPendingForm;
      if (!form) return;
      window.__pdpRecommendationPendingForm = null;
      const source = sourceFor(form);
      if (!source) return;

      const added = event.detail?.product;
      let price = 0;
      if (added?.price != null) price = Number(added.price) / 100;
      else if (added?.presentment_price != null) price = Number(added.presentment_price);

      const card = form.closest('.product-card');
      push({
        event: 'cart_recommendation_add',
        recommendation_source: source,
        recommendation_list: LISTS[source],
        source_product_id: sourceProductId(),
        ecommerce: {
          currency: currency(),
          value: price,
          items: [
            {
              item_id: String(form.querySelector('[name="id"]')?.value || ''),
              item_name: cardTitle(card),
              price,
              quantity: 1,
              item_list_name: LISTS[source],
              item_list_id: LISTS[source],
            },
          ],
        },
      });
    });

    document.addEventListener('product-ajax:error', () => {
      window.__pdpRecommendationPendingForm = null;
    });
  };

  return { observe, bind, tagForms };
})();

window.RecommendationTracking.bind();

if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        // Server-rendered (data-ssr): the cards are already in the HTML, so there is
        // nothing to fetch. Tracking and form tagging are wired in connectedCallback.
        if (this.hasAttribute('data-ssr')) return;

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      connectedCallback() {
        if (!this.hasAttribute('data-ssr') || this.ssrReady) return;
        this.ssrReady = true;
        if (!this.querySelector('.product-card')) return;
        window.RecommendationTracking?.tagForms(this);
        window.RecommendationTracking?.observe(this);
        this.dispatchEvent(new CustomEvent('recommendations:loaded'));
      }

      init() {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }

            if (recommendations.querySelector('.product-card')) {
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
              window.RecommendationTracking?.tagForms(this);
              window.RecommendationTracking?.observe(this);
            } else {
              if (recommendations.classList.contains('complementary-products')) {
                this.closest('.product__block').remove();
              } else {
                this.closest('.shopify-section').remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }
  );
}
